import { version } from '@/../package.json';
import { createApp, defineAsyncComponent, type App as VueApp } from 'vue';
import './style.css';
import axios, { AxiosError } from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as Sentry from '@sentry/vue';
import {
  faArrowLeft,
  faArrowRight,
  faBars,
  faBriefcase,
  faCalendar,
  faCamera,
  faCaretDown,
  faCaretLeft,
  faCaretRight,
  faCaretUp,
  faChartLine,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faClock,
  faCloudUploadAlt,
  faCommentSms,
  faCopy as faCopySolid,
  faDashboard,
  faDownLeftAndUpRightToCenter,
  faDownload,
  faDrawPolygon,
  faEdit,
  faEnvelope,
  faExclamationCircle,
  faExclamationTriangle,
  faExternalLink,
  faExternalLinkAlt,
  faEye,
  faEyeSlash,
  faFile,
  faFilePdf,
  faFlagUsa,
  faFolder,
  faGlobe,
  faHammer,
  faHeart,
  faImage,
  faMap,
  faMinus,
  faMobileScreen,
  faPaintBrush,
  faPause,
  faPen,
  faPhone,
  faPlane,
  faPlay,
  faPlus,
  faPrint,
  faQrcode,
  faReply,
  faScroll,
  faSearch,
  faSearchMinus,
  faShare,
  faSort,
  faSpinner,
  faStar,
  faStreetView,
  faSync,
  faTable,
  faTimes,
  faTrash,
  faTree,
  faUpRightAndDownLeftFromCenter,
  faUser,
  faUserGroup,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import {
  faCopy as faCopyRegular,
  faStar as faStarRegular,
} from '@fortawesome/free-regular-svg-icons';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import Toast, {
  type PluginOptions as VueToastificationPluginOptions,
} from 'vue-toastification';
import { i18n, interpolateNamedFallback } from '@/modules/i18n';

import App from './App.vue';
import MaintenanceApp from './maintenance/App.vue';
import router from './router';
import BaseButton from './components/BaseButton.vue';
import BaseInput from './components/BaseInput.vue';
import BaseText from './components/BaseText.vue';
import Badge from './components/Badge.vue';
import BasePill from './components/BasePill.vue';
import Tag from './components/Tag.vue';
import Modal from './components/Modal.vue';
import Authenticated from './layouts/Authenticated.vue';

// Icons

import BaseIcon from './components/BaseIcon.vue';
import { store } from './store';

// Toast
import 'vue-toastification/dist/index.css';

// Responsive
import { Vue3Mq } from 'vue3-mq';

// Popover
import { Dropdown, VTooltip, Menu } from 'floating-vue';
import 'floating-vue/dist/style.css';

import BaseSelect from './components/BaseSelect.vue';
import Spinner from './components/Spinner.vue';
import BaseCheckbox from './components/BaseCheckbox.vue';
import Tab from './components/tabs/Tab.vue';
import Tabs from './components/tabs/Tabs.vue';
import BaseRadio from './components/BaseRadio.vue';
import Unauthenticated from './layouts/Unauthenticated.vue';
import BaseLink from './components/BaseLink.vue';
import TreeMenu from '@/components/TreeMenu.vue';
import {
  getAndToastWarningMessage,
  shouldReportToSentry,
} from '@/utils/errors';
import { reloadForStaleChunk } from '@/utils/staleChunkReload';

const Datepicker = defineAsyncComponent(async () => {
  await import('@vuepic/vue-datepicker/dist/main.css');
  const mod = await import('@vuepic/vue-datepicker');
  return mod.default;
});

const VueTagsInput = defineAsyncComponent(async () => {
  const mod = await import('@sipec/vue3-tags-input');
  return mod.default;
});

const FormTree = defineAsyncComponent(
  () => import('./components/form/FormTree.vue'),
);

const CHUNK_LOAD_ERROR_PATTERN =
  /preloaderror|failed to fetch dynamically imported module|importing a module script failed|is not a valid javascript mime type|unable to preload css|unexpected token '<'.*<!doctype/i;
const THIRD_PARTY_SNIPPET_ERROR_PATTERN =
  /\/ekr\/snippet\.js|new .*inboundfilters|inboundfilters|i is undefined/i;

window.addEventListener('vite:preloadError', reloadForStaleChunk);

library.add(
  faArrowLeft,
  faArrowRight,
  faBars,
  faBriefcase,
  faCalendar,
  faCamera,
  faCaretDown,
  faCaretLeft,
  faCaretRight,
  faCaretUp,
  faChartLine,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faClock,
  faCloudUploadAlt,
  faCommentSms,
  faCopySolid,
  faDashboard,
  faDownLeftAndUpRightToCenter,
  faDownload,
  faDrawPolygon,
  faEdit,
  faEnvelope,
  faExclamationCircle,
  faExclamationTriangle,
  faExternalLink,
  faExternalLinkAlt,
  faEye,
  faEyeSlash,
  faFile,
  faFilePdf,
  faFlagUsa,
  faFolder,
  faGlobe,
  faHammer,
  faHeart,
  faImage,
  faMap,
  faMinus,
  faMobileScreen,
  faPaintBrush,
  faPause,
  faPen,
  faPhone,
  faPlane,
  faPlay,
  faPlus,
  faPrint,
  faQrcode,
  faReply,
  faScroll,
  faSearch,
  faSearchMinus,
  faShare,
  faSort,
  faSpinner,
  faStar,
  faStreetView,
  faSync,
  faTable,
  faTimes,
  faTrash,
  faTree,
  faUpRightAndDownLeftFromCenter,
  faUser,
  faUserGroup,
  faWrench,
  faCopyRegular,
  faStarRegular,
  faFacebook,
  faInstagram,
  faLinkedin,
  faXTwitter,
);

axios.defaults.withCredentials = true;
axios.defaults.baseURL = import.meta.env.VITE_APP_API_BASE_URL;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error instanceof AxiosError &&
      [400, 408, 409, 422, 502].includes(error.response?.status as number) &&
      // A caller that handles this error itself (e.g. the duplicate check).
      !(error.config as { skipErrorToast?: boolean } | undefined)
        ?.skipErrorToast
    ) {
      getAndToastWarningMessage(error);
    }
    throw error;
  },
);
const buildApp = (app: VueApp) =>
  app
    .component('FontAwesomeIcon', FontAwesomeIcon as any)
    .component('CcuIcon', BaseIcon)
    .component('BaseButton', BaseButton)
    .component('BaseInput', BaseInput)
    .component('BaseText', BaseText)
    .component('BaseLink', BaseLink)
    .component('BaseRadio', BaseRadio)
    .component('Badge', Badge)
    .component('BasePill', BasePill)
    .component('Tag', Tag)
    .component('TagInput', VueTagsInput)
    .component('AuthenticatedLayout', Authenticated)
    .component('UnauthenticatedLayout', Unauthenticated)
    .component('BaseSelect', BaseSelect)
    .component('BaseCheckbox', BaseCheckbox)
    .component('Modal', Modal)
    .component('VPopover', Dropdown)
    .component('VMenu', Menu)
    .component('Spinner', Spinner)
    .component('FormTree', FormTree)
    .component('TreeMenu', TreeMenu)
    .component('Tabs', Tabs)
    .component('Tab', Tab)
    .component('Datepicker', Datepicker)
    .directive('tooltip', VTooltip)
    .use(store)
    // provide axios globally
    .provide('axios', axios)
    .use(Vue3Mq)
    .use(router)
    .use(i18n)
    .use(Toast, {
      timeout: 10_000,
      shareAppContext: true,
    } as VueToastificationPluginOptions);

const installI18nFallbackInterpolation = (app: VueApp) => {
  // Wrap the global `$t` AND the i18n composer's `t` so missing-key
  // fallbacks ("~~English with {n}") still get their named placeholders
  // interpolated AND the `~~` source-code sentinel stripped before reaching
  // the UI. vue-i18n short-circuits its compiler on the missing-key path
  // with formatFallbackMessages: true, so the raw key would otherwise leak
  // through.
  const wrap = <T extends (...args: unknown[]) => unknown>(originalT: T): T => {
    return function patchedT(this: unknown, ...args: unknown[]) {
      const result = originalT.apply(this, args);
      const named = args[1];
      const namedObj =
        named && typeof named === 'object' && !Array.isArray(named)
          ? (named as Record<string, unknown>)
          : undefined;
      return interpolateNamedFallback(result, namedObj);
    } as T;
  };

  const props = app.config.globalProperties;
  if (typeof props.$t === 'function') {
    props.$t = wrap(
      props.$t as (...a: unknown[]) => unknown,
    ) as typeof props.$t;
  }

  // `useI18n()` returns the global composer by default (legacy: false), so
  // patching `i18n.global.t` covers component-level `t()` calls too.
  const composer = i18n.global as unknown as {
    t: (...a: unknown[]) => unknown;
  };
  if (typeof composer.t === 'function') {
    composer.t = wrap(composer.t.bind(composer));
  }
};

const initSentry = (vueApp: VueApp) =>
  Sentry.init({
    app: vueApp,
    dsn: 'https://33b5cc9258d64b5cb2a8084af5df4051@o317954.ingest.sentry.io/4504774609141760',
    release: `crisiscleanup-4-web@v${version}`,
    environment: import.meta.env.VITE_APP_STAGE,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/(api\.|app\.|)(dev\.|staging\.|)crisiscleanup\.(org|io)/,
      /^\//,
    ],
    tracesSampleRate: 0.15,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 0.2,
    trackComponents: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    beforeSend(event, hint) {
      const original = hint?.originalException as any;
      if (original && !shouldReportToSentry(original)) return null;
      const message =
        (typeof original?.message === 'string' && original.message) ||
        event.exception?.values?.[0]?.value ||
        '';
      if (message && CHUNK_LOAD_ERROR_PATTERN.test(message)) return null;
      const stacktrace = event.exception?.values
        ?.flatMap((value) => value.stacktrace?.frames ?? [])
        .map((frame) => frame.filename ?? '')
        .join('\n');
      if (THIRD_PARTY_SNIPPET_ERROR_PATTERN.test(`${message}\n${stacktrace}`)) {
        return null;
      }
      return event;
    },
  });

const entrypoint =
  import.meta.env.VITE_APP_ENTRY === 'maintenance' ? MaintenanceApp : App;
const app = buildApp(createApp(entrypoint));
installI18nFallbackInterpolation(app);

if (import.meta.env.PROD) {
  initSentry(app);
}

app.mount('#app');
